import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Clock, ArrowRight } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

export default function Blog() {
  usePageTitle("Blog & Field Notes", "Practical articles on excavation, concrete, drainage and property care for BC homeowners — written by the AV Contracting crew.");
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
  }, []);

  return (
    <div className="bg-[#F4F5F6] min-h-screen" data-testid="blog-page">
      <section className="bg-[#23282B] av-grid text-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#9CC4DB] font-bold">AV Contracting · Field Notes</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none">The Blog</h1>
          <p className="mt-4 max-w-2xl text-white/75 text-base md:text-lg">
            Straight answers on excavation, concrete, drainage and property care in coastal BC — written for homeowners, not engineers.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-16 py-12 md:py-16">
        {posts === null && (
          <div className="text-center py-16 text-[#2C3539]/60" data-testid="blog-loading">Loading posts…</div>
        )}
        {posts !== null && posts.length === 0 && (
          <div className="border-2 border-dashed border-[#2C3539]/20 p-12 text-center bg-white" data-testid="blog-empty">
            <Newspaper size={34} className="mx-auto text-[#2F5F80]" />
            <div className="mt-3 font-display text-2xl uppercase text-[#2C3539]">First post coming soon</div>
            <p className="text-sm text-[#2C3539]/60 mt-1">We publish a new homeowner guide every week — check back shortly.</p>
          </div>
        )}
        {posts !== null && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-list">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white border-2 border-[#2C3539]/10 hover:border-[#2F5F80] transition-colors flex flex-col overflow-hidden"
              >
                {p.header_image && (
                  <div className="aspect-[16/8] overflow-hidden bg-[#23282B]">
                    <img
                      src={assetUrl(p.header_image)}
                      alt={p.title}
                      loading="lazy"
                      data-testid={`blog-card-img-${p.slug}`}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#2F5F80] font-bold">
                  {p.published_at?.slice(0, 10)} · {p.reading_time} min read
                </div>
                <h2 className="mt-3 font-display text-2xl uppercase leading-tight text-[#23282B] group-hover:text-[#2F5F80] transition-colors">
                  {p.title}
                </h2>
                <p className="mt-3 text-sm text-[#2C3539]/75 leading-relaxed flex-1">{p.excerpt}</p>
                {p.tags?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider bg-[#2F5F80]/10 text-[#2F5F80] px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                )}
                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#2F5F80]">
                  Read Post <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
